<template>
  <div class="ivt-root">
    <!-- Above the table, like Generate Invoices' selection action bar. -->
    <InvoiceActionsToolbar
      :count="selection.selectedCount.value"
      :all-selected="selection.allSelected.value"
      :selecting-all="selection.selectingAll.value"
      :actions="toolbarActions"
      :busy-action="busyAction"
      noun="invoices"
      @action="(key) => runAction(key, selection.selectedIds.value, true)"
      @clear="selection.clear"
    />

    <InvoiceViewAccordionTable
      ref="accordionRef"
      :columns="InvoiceViewColumns"
      :items="itemsProvider"
      :total="total"
      :initial-page-size="25"
      :page-size-options="[25, 50, 100, 200]"
      selectable
      @row-toggled="handleRowToggled"
    >
      <template v-slot:head(select)>
        <InvoicingSelectCheckbox
          :checked="selection.headerState.value === 'all'"
          :indeterminate="selection.headerState.value === 'some'"
          :disabled="!total || selection.selectingAll.value"
          aria-label="Select all invoices"
          @toggle="selection.toggleHeader"
        />
      </template>
      <template v-slot:cell(select)="{ item }">
        <InvoicingSelectCheckbox
          :checked="selection.isSelected(item)"
          :aria-label="`Select invoice ${item.invoice_reference || ''}`"
          @toggle="selection.toggleRow(item)"
        />
      </template>

      <template v-slot:cell(invoice_ref)="{ item }">
        <span class="ivt-ref">{{ item.invoice_reference || '—' }}</span>
      </template>

      <template v-slot:cell(amount)="{ item }">
        <span class="ivt-amount">{{ formatCurrency(item.amount, item.currency_type) }}</span>
      </template>

      <template v-slot:cell(credit)="{ item }">{{ formatCurrency(item.credit, item.currency_type) }}</template>

      <!-- Hover shows the read-only payment log (LIQ-2075). -->
      <template v-slot:cell(amount_paid)="{ item }"><InvoicePaymentsPopover :invoice="item" /></template>

      <!-- Red while anything is owing, green once settled (LIQ-2076). -->
      <template v-slot:cell(remaining_balance)="{ item }">
        <span :class="item.remaining_balance > 0 ? 'ivt-owing' : 'ivt-settled'">
          {{ formatCurrency(item.remaining_balance, item.currency_type) }}
        </span>
      </template>

      <template v-slot:cell(invoice_status)="{ item }">
        <span class="ivt-status" :class="`ivt-status--${statusClass(item.invoice_status)}`">{{ item.invoice_status || '—' }}</span>
      </template>

      <template v-slot:cell(managing_agent)="{ item }">{{ item.managing_agent || '—' }}</template>
      <template v-slot:cell(tpa_name)="{ item }">{{ item.tpa_name || '—' }}</template>
      <template v-slot:cell(submission_period)="{ item }">{{ item.submission_period }}</template>
      <template v-slot:cell(invoice_date)="{ item }">{{ item.invoice_date_display }}</template>
      <template v-slot:cell(currency_type)="{ item }">{{ item.currency_type || '—' }}</template>
      <template v-slot:cell(due_date)="{ item }">{{ item.due_date_display }}</template>
      <template v-slot:cell(billing_type)="{ item }">{{ item.billing_type || '—' }}</template>
      <template v-slot:cell(syndicate_number)="{ item }">{{ item.syndicate_number || '—' }}</template>

      <template v-slot:cell(row_actions)="{ item }">
        <b-dropdown
          size="sm"
          variant="link"
          no-caret
          right
          toggle-class="ivt-row-actions__toggle"
          menu-class="ivt-row-actions__menu"
          :disabled="!!busyAction"
          boundary="window"
        >
          <template #button-content><b-icon icon="three-dots" /></template>
          <b-dropdown-item
            v-for="action in rowActionsFor(item)"
            :key="action.key"
            :disabled="action.disabled"
            :title="action.disabled ? action.disabledReason : ''"
            @click="runAction(action.key, [String(item.invoice_id)], false, item)"
          >
            {{ action.label }}
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <template v-slot:row-detail="{ item }">
        <div class="ivt-charges">
          <div v-if="loadingInvoiceIds[item.invoice_id]" class="ivt-charges__loading">Loading charges…</div>
          <table v-else class="ivt-charges__table">
            <thead>
              <tr>
                <th v-for="col in AccordionChargeColumns" :key="col.key">{{ col.label }}</th>
              </tr>
              <!-- Per-column filters. This invoice's charges are all loaded
                   at once (unpaginated), so they're filtered right here. -->
              <tr v-if="chargesFor(item.invoice_id).length" class="ivt-charges__filters">
                <th v-for="(col, i) in AccordionChargeColumns" :key="`filter-${col.key}`">
                  <div class="ivt-charges__filter-cell">
                    <select
                      v-if="col.options"
                      class="ivt-charges__filter"
                      :value="accordionFilterValue(item.invoice_id, col.key)"
                      :aria-label="`Filter ${col.label}`"
                      @change="setAccordionFilter(item.invoice_id, col.key, $event.target.value)"
                    >
                      <option value="">All</option>
                      <option v-for="opt in col.options" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                    </select>
                    <input
                      v-else
                      type="text"
                      class="ivt-charges__filter"
                      placeholder="Filter"
                      :value="accordionFilterValue(item.invoice_id, col.key)"
                      :aria-label="`Filter ${col.label}`"
                      @input="setAccordionFilter(item.invoice_id, col.key, $event.target.value)"
                    />
                    <button
                      v-if="i === AccordionChargeColumns.length - 1 && hasAccordionFilters(item.invoice_id)"
                      type="button"
                      class="ivt-charges__filter-clear"
                      title="Clear filters"
                      @click="clearAccordionFilters(item.invoice_id)"
                    >✕</button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!chargesFor(item.invoice_id).length">
                <td :colspan="AccordionChargeColumns.length" class="ivt-charges__empty">
                  No charges found for this invoice.
                </td>
              </tr>
              <tr v-else-if="!filteredChargesFor(item.invoice_id).length">
                <td :colspan="AccordionChargeColumns.length" class="ivt-charges__empty">
                  No charges match these filters.
                </td>
              </tr>
              <tr v-for="charge in filteredChargesFor(item.invoice_id)" :key="charge.id">
                <td class="ivt-mono">{{ charge.bdx_key || '—' }}</td>
                <td class="ivt-mono">{{ charge.invoice_reference || '—' }}</td>
                <td>{{ formatReportPeriod(charge.reporting_period) || '—' }}</td>
                <td>{{ charge.broker || '—' }}</td>
                <td>{{ charge.billing_type || '—' }}</td>
                <td>{{ charge.syndicate_number || '—' }}</td>
                <td>{{ charge.charge_description || '—' }}</td>
                <td>
                  <!-- LIQ-2465: the one place accordion rows can be acted
                       on — a TPA ticks keys as Received here or on the
                       Itemized view (same flag); everyone else reads it. -->
                  <InvoicingSelectCheckbox
                    v-if="isTpa && !item.is_archived"
                    :checked="!!charge.received"
                    :disabled="!!savingReceived[charge.charge_id]"
                    :aria-label="`Mark ${charge.bdx_key || 'key'} as received`"
                    @toggle="toggleChargeReceived(item.invoice_id, charge)"
                  />
                  <span v-else :class="charge.received ? 'ivt-settled' : 'ivt-muted'">{{ charge.received ? 'Yes' : 'No' }}</span>
                </td>
                <td>{{ charge.currency_type || '—' }}</td>
                <td class="ivt-charges__amount">{{ formatCurrency(charge.amount, charge.currency_type) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="chargesFor(item.invoice_id).length">
              <tr>
                <td :colspan="AccordionChargeColumns.length - 1" class="ivt-charges__subtotal-label">
                  Subtotal —
                  <template v-if="hasAccordionFilters(item.invoice_id)">
                    {{ filteredChargesFor(item.invoice_id).length }} of
                  </template>
                  {{ chargesFor(item.invoice_id).length }} key{{ chargesFor(item.invoice_id).length !== 1 ? 's' : '' }}
                </td>
                <td class="ivt-charges__amount ivt-charges__subtotal-value">
                  {{ formatCurrency(subtotalFor(item.invoice_id), item.currency_type) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </template>
    </InvoiceViewAccordionTable>

    <InvoicePaymentModal
      :invoice="paymentInvoice"
      @close="paymentInvoice = null"
      @saved="handlePaymentSaved"
      @updated="handlePaymentUpdated"
    />
  </div>
</template>

<script>
import { isCancel } from 'axios'
import { defineComponent, ref, computed, getCurrentInstance } from '@vue/composition-api'

import InvoicingService from '@services/InvoicingService'
import InvoiceViewAccordionTable from './InvoiceViewAccordionTable.vue'
import InvoiceActionsToolbar from './InvoiceActionsToolbar.vue'
import InvoicingSelectCheckbox from './InvoicingSelectCheckbox.vue'
import InvoicePaymentModal from './InvoicePaymentModal.vue'
import InvoicePaymentsPopover from './InvoicePaymentsPopover.vue'
import { formatCurrency, formatReportPeriod, saveBlobResponse } from './useInvoiceExport'
import { getInvoiceViewColumns, getAccordionChargeColumns } from './InvoicingTab.constants'
import useInvoiceFilterOptions, { MULTI_VALUE_SEPARATOR } from './useInvoiceFilterOptions'
import {
  useInvoicingRole,
  useInvoicingSelection,
  criteriaSignature,
  requestErrorMessage,
  responseBody,
} from './useInvoicingSelection'

const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatSubmissionPeriod = (month, year) => {
  if (!month || !year) return '—'
  return `${MONTH_NAMES_SHORT[Number(month) - 1] || month} ${year}`
}

// Dates from the backend arrive as plain 'YYYY-MM-DD' strings — parsed
// with an explicit time to avoid the browser reinterpreting a date-only
// string in UTC and shifting it a day in negative-offset timezones.
const parseDateOnly = (isoDateStr) => {
  if (!isoDateStr) return null
  const d = new Date(`${isoDateStr}T00:00:00`)
  return isNaN(d.getTime()) ? null : d
}

const formatDateDisplay = (isoDateStr) => {
  const d = parseDateOnly(isoDateStr)
  return d ? `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}` : '—'
}

// Same setting already used for the PDF export's due date
// (useInvoiceExport.js) — currently 60 days.
const PAYMENT_TERMS_DAYS = Number(process.env.VUE_APP_INVOICE_PAYMENT_TERMS_DAYS) || 60

// Due Date = dispatched_at + PAYMENT_TERMS_DAYS (not approved_at — see
// InvoiceListView.DUE_DATE_DAYS_FROM_DISPATCH on the backend, which this
// mirrors for display).
const computeDueDateDisplay = (dispatchedAtIso) => {
  const d = parseDateOnly(dispatchedAtIso)
  if (!d) return '—'
  d.setDate(d.getDate() + PAYMENT_TERMS_DAYS)
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`
}

// FE column key -> backend query param name (InvoiceListView). Range
// columns (invoice_date/due_date) arrive here already split into _gt/_lt
// by InvoiceViewAccordionTable.vue's filter expansion, so their keys
// already match the backend's param names 1:1. submission_period isn't
// listed here — it needs its single date value parsed into
// billing_month/billing_year instead, handled separately below.
const FILTER_PARAM_MAP = {
  invoice_ref: 'invoice_reference',
  amount: 'amount',
  managing_agent: 'managing_agent',
  tpa_name: 'tpa_name',
  currency_type: 'currency_type',
  invoice_date_gt: 'invoice_date_gt',
  invoice_date_lt: 'invoice_date_lt',
  due_date_gt: 'due_date_gt',
  due_date_lt: 'due_date_lt',
  invoice_status: 'invoice_status',
  credit: 'credit',
  amount_paid: 'amount_paid',
  remaining_balance: 'remaining_balance',
  billing_type: 'billing_type',
  syndicate_number: 'syndicate_number',
}

const STATUS_CLASSES = { Paid: 'paid', 'Partially Paid': 'partial', Unpaid: 'unpaid', Archived: 'archived' }

export default defineComponent({
  name: 'InvoiceViewTable',

  components: {
    InvoiceViewAccordionTable,
    InvoiceActionsToolbar,
    InvoicingSelectCheckbox,
    InvoicePaymentModal,
    InvoicePaymentsPopover,
  },

  props: {
    // Ask for the per-currency invoiced / paid / outstanding / due totals
    // behind InvoicingTab's summary cards (LIQ-2656).
    withTotals: { type: Boolean, default: false },
  },

  emits: ['summary-changed'],

  setup(props, { emit }) {
    const proxy = getCurrentInstance().proxy
    const { isSuperuser, isTpa } = useInvoicingRole()

    const filterOptions = useInvoiceFilterOptions()
    const InvoiceViewColumns = computed(() =>
      getInvoiceViewColumns({ isSuperuser: isSuperuser.value, filterOptions: filterOptions.value }))
    const AccordionChargeColumns = getAccordionChargeColumns()

    const accordionRef = ref(null)
    const total = ref(0)
    const exporting = ref(false)

    // Charges are no longer preloaded — each invoice's own charges are
    // fetched on first expand and cached here, keyed by invoice_id.
    // Always reassigned (not mutated in place) since Vue 2's reactivity
    // doesn't track new keys added to an existing plain object.
    const chargesByInvoiceId = ref({})
    const loadingInvoiceIds = ref({})

    const chargesFor = (invoiceId) => chargesByInvoiceId.value[invoiceId] || []

    // Accordion per-column filters, per invoice: { [invoiceId]: { [columnKey]: value } }.
    // Reassigned rather than mutated — Vue 2 doesn't track new keys.
    const accordionFilters = ref({})

    const accordionFilterValue = (invoiceId, key) =>
      (accordionFilters.value[invoiceId] || {})[key] || ''

    const setAccordionFilter = (invoiceId, key, value) => {
      accordionFilters.value = {
        ...accordionFilters.value,
        [invoiceId]: { ...(accordionFilters.value[invoiceId] || {}), [key]: value },
      }
    }

    const hasAccordionFilters = (invoiceId) =>
      Object.values(accordionFilters.value[invoiceId] || {}).some((v) => v !== '' && v !== null && v !== undefined)

    const clearAccordionFilters = (invoiceId) => {
      const next = { ...accordionFilters.value }
      delete next[invoiceId]
      accordionFilters.value = next
    }

    // What each filterable cell holds, as text, for a contains-match —
    // the value the cell actually shows where it's formatted (amount).
    const accordionCellText = {
      amount: (c) => (c.amount === null || c.amount === undefined ? '' : String(c.amount)),
      reporting_period: (c) => formatReportPeriod(c.reporting_period),
    }

    const filteredChargesFor = (invoiceId) => {
      const filters = accordionFilters.value[invoiceId]
      const charges = chargesFor(invoiceId)
      if (!filters) return charges
      return charges.filter((charge) => Object.entries(filters).every(([key, value]) => {
        if (value === '' || value === null || value === undefined) return true
        if (key === 'received') return (value === 'yes') === !!charge.received
        if (key === 'billing_type') return (charge.billing_type || '') === value
        const cell = accordionCellText[key] ? accordionCellText[key](charge) : charge[key]
        return String(cell === null || cell === undefined ? '' : cell).toLowerCase().includes(String(value).toLowerCase())
      }))
    }

    // Follows the accordion's filters, so it always adds up the rows shown.
    const subtotalFor = (invoiceId) =>
      filteredChargesFor(invoiceId).reduce((sum, c) => sum + (Number(c.amount) || 0), 0)

    const handleRowToggled = async (item) => {
      const invoiceId = item.invoice_id
      if (chargesByInvoiceId.value[invoiceId] || loadingInvoiceIds.value[invoiceId]) return

      loadingInvoiceIds.value = { ...loadingInvoiceIds.value, [invoiceId]: true }
      try {
        const resp = await InvoicingService.getInvoiceCharges({
          invoice_id: invoiceId,
          // Every charge of the invoice: the search box doesn't narrow an
          // accordion, only its own column filters below do.
          // An archived invoice's charges are hidden like the invoice
          // itself unless asked for — only a Superuser ever lists one.
          include_archived: item.is_archived || undefined,
        })
        const inner = resp.data && resp.data.data
        const rows = inner && inner.data
        chargesByInvoiceId.value = { ...chargesByInvoiceId.value, [invoiceId]: Array.isArray(rows) ? rows : [] }
      } catch (e) {
        console.error('Failed to load charges for invoice', invoiceId, e)
        chargesByInvoiceId.value = { ...chargesByInvoiceId.value, [invoiceId]: [] }
      } finally {
        loadingInvoiceIds.value = { ...loadingInvoiceIds.value, [invoiceId]: false }
      }
    }

    // Adds the display-only fields the cell templates read — pure
    // transforms of what the backend already returns (dispatched_at,
    // approved_at, billing_month/year) — computed once per fetched row
    // rather than per-cell render.
    const decorate = (inv) => ({
      ...inv,
      id: inv.invoice_id,
      submission_period: formatSubmissionPeriod(inv.billing_month, inv.billing_year),
      invoice_date_display: formatDateDisplay(inv.approved_at),
      due_date_display: computeDueDateDisplay(inv.dispatched_at),
    })

    // Kept in sync on every itemsProvider call (page/sort/filter/search
    // change) so exportXlsx can re-issue the exact same query, minus
    // pagination, as an Excel download — see InvoicingTab.vue's Export
    // button. Also what "select all" re-issues as a selection fetch.
    const lastQueryParams = ref({ dispatched: true })

    // The rows on the current page, by id — the full invoice row Add
    // Payment and the per-row actions need, which a select-all selection
    // only carries the action-eligibility fields of.
    const loadedRowsById = ref({})

    const selection = useInvoicingSelection({
      idOf: (row) => String(row.invoice_id),
      metaOf: (row) => ({
        invoice_id: row.invoice_id,
        invoice_status: row.invoice_status,
        is_archived: !!row.is_archived,
      }),
      fetchAllMatching: async () => {
        const { sort_by, sort_desc, ...criteria } = lastQueryParams.value
        const resp = await InvoicingService.getInvoiceSelection(criteria)
        return responseBody(resp).data || []
      },
    })

    let lastCriteria = null

    const itemsProvider = async (context) => {
      const { filter, filters, currentPage, perPage, sortBy, sortDesc } = context

      // A selection survives paging and sorting, but not a change to what
      // the table is showing (LIQ-2071 AC16/17).
      const criteria = criteriaSignature(filter, filters)
      if (lastCriteria !== null && criteria !== lastCriteria) selection.clear()
      lastCriteria = criteria

      const filterParams = {}
      if (filters && filters.length) {
        filters.forEach((f) => {
          // Submission Period's picker yields one month/year, not a
          // range — split into the two exact-match params the backend
          // expects instead of a generic 'submission_period' param.
          if (f.key === 'submission_period') {
            // Read straight from the 'YYYY-MM-DD' value, not new Date(value):
            // a bare date string is parsed as UTC midnight, which in a
            // browser behind UTC is still the previous day — so picking
            // Nov 2026 filtered October.
            const picked = /^(\d{4})-(\d{2})/.exec(f.value || '')
            if (picked) {
              filterParams.billing_month = Number(picked[2])
              filterParams.billing_year = Number(picked[1])
            }
            return
          }
          const paramName = FILTER_PARAM_MAP[f.key]
          if (!paramName) return
          // A multi-select filter (LIQ-2656) sends every picked value.
          if (f.multi) filterParams[`${paramName}_in`] = f.value.join(MULTI_VALUE_SEPARATOR)
          else filterParams[paramName] = f.value
        })
      }

      const queryParams = {
        dispatched: true,
        sort_by: sortBy,
        sort_desc: !!sortDesc,
        search: filter || undefined,
        ...filterParams,
      }
      lastQueryParams.value = queryParams

      try {
        const resp = await InvoicingService.getApprovedInvoices({
          ...queryParams,
          page: currentPage,
          page_size: perPage,
          ...(props.withTotals ? { include_totals: true } : {}),
        }, { signal: context.signal })
        const inner = resp.data && resp.data.data
        const rows = inner && inner.data
        total.value = (inner && inner.count) || 0
        selection.total.value = total.value
        // total_amount is Sum(amount) over every invoice matching the
        // current filters — not just this page — so InvoicingTab.vue's
        // Total Invoiced card can track the filtered set, not the grand
        // total. Re-emitted on every fetch (page/sort/filter change).
        emit('summary-changed', {
          totalAmount: (inner && inner.total_amount) || 0,
          count: total.value,
          totalsByCurrency: (inner && inner.totals_by_currency) || {},
        })
        const decorated = Array.isArray(rows) ? rows.map(decorate) : []
        loadedRowsById.value = Object.fromEntries(decorated.map((r) => [String(r.invoice_id), r]))
        // Rows changed in place by an action (status, archived) keep their
        // selection but need their eligibility fields refreshed.
        selection.refreshMeta(decorated)
        return decorated
      } catch (e) {
        // Superseded by a newer search/filter/page (see BaseDataTableTable.vue).
        if (isCancel(e)) return []
        console.error('Failed to load invoices:', e)
        total.value = 0
        selection.total.value = 0
        emit('summary-changed', { totalAmount: 0, count: 0, totalsByCurrency: {} })
        // An empty table alone reads as "nothing matches" - say it failed.
        toast(await requestErrorMessage(e, 'Could not load invoices. Please try again.'), 'danger', 'Error')
        return []
      }
    }

    // ── Actions (LIQ-2072/2073) ──────────────────────────────────────────

    const busyAction = ref(null)
    const paymentInvoice = ref(null)

    const toast = (message, variant = 'success', title = 'Invoices') => {
      proxy.$bvToast && proxy.$bvToast.toast(message, { title, variant, autoHideDelay: 5000, solid: true })
    }

    const confirm = (message, okTitle, okVariant = 'primary') =>
      proxy.$bvModal.msgBoxConfirm(message, {
        title: 'Please confirm', size: 'sm', centered: true, okTitle, okVariant, cancelTitle: 'Cancel',
      })

    const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

    // Which actions a set of selected rows allows. Shared by the toolbar
    // (the whole selection) and the per-row menu (one row), so both always
    // agree. Add Payment is toolbar-only (LIQ-2077).
    const actionsFor = (rows, { includePayment }) => {
      const list = [
        { key: 'xls', label: '↓ XLS' },
        { key: 'pdf', label: '↓ PDF' },
        {
          key: 'resend', label: '✉ Resend',
          disabled: rows.some((r) => r.is_archived), disabledReason: "Archived invoices can't be resent.",
        },
      ]
      if (!isSuperuser.value) return list

      if (includePayment) {
        const single = rows.length === 1 ? rows[0] : null
        let reason = ''
        if (rows.length > 1) reason = 'Add Payment works on one invoice at a time.'
        else if (single && single.is_archived) reason = 'An archived invoice cannot take a payment.'
        // A paid invoice still opens the modal, to view or correct its
        // payments (LIQ-2074) — it just can't take a new one.
        const label = single && single.invoice_status === 'Paid' ? 'Payments' : 'Add Payment'
        list.push({ key: 'payment', label, variant: 'success', disabled: !!reason, disabledReason: reason })
      }

      const allArchived = rows.length > 0 && rows.every((r) => r.is_archived)
      if (allArchived) {
        list.push({ key: 'unarchive', label: 'Unarchive' })
      } else {
        const anyArchived = rows.some((r) => r.is_archived)
        list.push({
          key: 'archive', label: 'Archive',
          disabled: anyArchived, disabledReason: 'Some selected invoices are already archived.',
        })
      }
      return list
    }

    const toolbarActions = computed(() => actionsFor(selection.selectedRows.value, { includePayment: true }))
    const rowActionsFor = (item) => actionsFor([item], { includePayment: false })

    const statusClass = (status) => STATUS_CLASSES[status] || 'unpaid'

    // Every action ends by refreshing the table (statuses may have moved
    // on) and, when it came from the toolbar, clearing the selection
    // (LIQ-2071 AC20).
    const finishAction = (fromToolbar) => {
      if (fromToolbar) selection.clear()
      chargesByInvoiceId.value = {}
      if (accordionRef.value) accordionRef.value.refresh()
    }

    const metaFor = (ids) => ids.map((id) => selection.selected.value[id] || loadedRowsById.value[id]).filter(Boolean)

    const download = async (ids, docType, includeArchived) => {
      const resp = await InvoicingService.downloadInvoiceDocuments({ invoiceIds: ids, docType, includeArchived })
      saveBlobResponse(resp, ids.length === 1 ? `invoice.${docType === 'pdf' ? 'pdf' : 'xlsx'}` : `invoices-${docType}.zip`)
      const missing = Number(resp.headers && resp.headers['x-missing-invoice-count']) || 0
      if (missing) {
        toast(`${plural(missing, 'invoice')} had no stored ${docType.toUpperCase()} and ${missing === 1 ? 'was' : 'were'} left out.`, 'warning', 'Download')
      }
    }

    const resend = async (ids) => {
      const ok = await confirm(`Resend the invoice email for ${plural(ids.length, 'invoice')} to the original recipients?`, 'Resend')
      if (!ok) return false
      const resp = await InvoicingService.resendInvoices({ invoiceIds: ids })
      const result = responseBody(resp).data || {}
      let message = `${plural(result.queued || 0, 'invoice email')} queued to resend.`
      if (result.without_billing_contact) {
        message += ` ${plural(result.without_billing_contact, 'invoice')} ${result.without_billing_contact === 1 ? 'has' : 'have'} no billing contact and will be skipped.`
      }
      toast(message, result.without_billing_contact ? 'warning' : 'success', 'Resend')
      return true
    }

    const archive = async (ids, archived) => {
      const verb = archived ? 'Archive' : 'Unarchive'
      const message = archived
        ? `Archive ${plural(ids.length, 'invoice')}? ${ids.length === 1 ? 'It' : 'They'} will be hidden from every invoice listing until unarchived. Nothing is deleted.`
        : `Unarchive ${plural(ids.length, 'invoice')}? ${ids.length === 1 ? 'It' : 'They'} will return to the invoice listings.`
      const ok = await confirm(message, verb, archived ? 'danger' : 'primary')
      if (!ok) return false
      const resp = await InvoicingService.archiveInvoices(ids, archived)
      const result = responseBody(resp).data || {}
      toast(`${plural(result.updated || 0, 'invoice')} ${archived ? 'archived' : 'unarchived'}.`, 'success', verb)
      return true
    }

    const openPayment = async (id) => {
      let row = loadedRowsById.value[id]
      if (!row) {
        // Selected via "select all" from another page — fetch the one row.
        const resp = await InvoicingService.getApprovedInvoices({ dispatched: true, invoice_id_in: id })
        const rows = responseBody(resp).data || []
        row = rows.length ? decorate(rows[0]) : null
      }
      if (row) paymentInvoice.value = row
      else toast('That invoice could not be loaded.', 'danger', 'Add Payment')
    }

    const runAction = async (key, ids, fromToolbar, item = null) => {
      if (!ids.length || busyAction.value) return
      const rows = item ? [item] : metaFor(ids)
      const includeArchived = rows.some((r) => r.is_archived)

      if (key === 'payment') {
        await openPayment(ids[0])
        return
      }

      busyAction.value = key
      try {
        let done = true
        if (key === 'xls' || key === 'pdf') await download(ids, key, includeArchived)
        else if (key === 'resend') done = await resend(ids)
        else if (key === 'archive') done = await archive(ids, true)
        else if (key === 'unarchive') done = await archive(ids, false)
        if (done) finishAction(fromToolbar)
      } catch (e) {
        console.error(`Invoice action ${key} failed:`, e)
        toast(await requestErrorMessage(e, 'Something went wrong. Please try again.'), 'danger', 'Error')
      } finally {
        busyAction.value = null
      }
    }

    const handlePaymentSaved = () => {
      paymentInvoice.value = null
      toast('Payment recorded.', 'success', 'Add Payment')
      finishAction(true)
    }

    // An edited payment keeps the modal open, so its summary takes the
    // invoice's new totals from Lincoln's response; the list reloads behind it.
    const handlePaymentUpdated = (invoice) => {
      // A late response for an invoice the modal no longer shows only refreshes the table.
      if (paymentInvoice.value && invoice && String(paymentInvoice.value.invoice_id) === String(invoice.invoice_id)) {
        paymentInvoice.value = {
          ...paymentInvoice.value,
          amount_paid: invoice.amount_paid,
          remaining_balance: invoice.remaining_balance,
          invoice_status: invoice.payment_status,
        }
      }
      toast('Payment updated.', 'success', 'Edit Payment')
      finishAction(false)
    }

    // ── Received (LIQ-2465, TPA only) ────────────────────────────────────

    const savingReceived = ref({})

    const toggleChargeReceived = async (invoiceId, charge) => {
      const chargeId = charge.charge_id
      const received = !charge.received
      savingReceived.value = { ...savingReceived.value, [chargeId]: true }
      try {
        await InvoicingService.markChargesReceived([chargeId], received)
        chargesByInvoiceId.value = {
          ...chargesByInvoiceId.value,
          [invoiceId]: chargesFor(invoiceId).map((c) => (c.charge_id === chargeId ? { ...c, received } : c)),
        }
      } catch (e) {
        toast(await requestErrorMessage(e, 'Could not update Received. Please try again.'), 'danger', 'Received')
      } finally {
        savingReceived.value = { ...savingReceived.value, [chargeId]: false }
      }
    }

    // Re-issues the current filter/search/sort as a full (unpaginated)
    // Excel download — called from InvoicingTab.vue's shared Export
    // button via this component's template ref.
    const exportXlsx = async () => {
      exporting.value = true
      try {
        const resp = await InvoicingService.exportApprovedInvoicesXlsx(lastQueryParams.value)
        saveBlobResponse(resp, 'invoices.xlsx')
      } catch (e) {
        console.error('Failed to export invoices:', e)
        throw e
      } finally {
        exporting.value = false
      }
    }

    return {
      InvoiceViewColumns,
      AccordionChargeColumns,

      accordionRef,
      total,
      itemsProvider,
      handleRowToggled,

      loadingInvoiceIds,
      chargesFor,
      subtotalFor,
      filteredChargesFor,
      formatReportPeriod,
      accordionFilterValue,
      setAccordionFilter,
      hasAccordionFilters,
      clearAccordionFilters,

      selection,
      toolbarActions,
      rowActionsFor,
      busyAction,
      runAction,
      statusClass,

      paymentInvoice,
      handlePaymentSaved,
      handlePaymentUpdated,

      isTpa,
      savingReceived,
      toggleChargeReceived,

      exporting,
      exportXlsx,

      formatCurrency,
    }
  },
})
</script>

<style scoped>
.ivt-ref { color: #1c4a50; font-size: 10px; font-family: monospace; }
.ivt-amount { font-weight: 600; color: #1c4a50; }
.ivt-amount--zero { color: #8898aa; font-weight: 400; }
.ivt-muted { color: #8898aa; }
.ivt-owing { color: #f5365c; font-weight: 600; }
.ivt-settled { color: #2dce89; font-weight: 600; }

.ivt-status {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}
.ivt-status--unpaid { background: #fdecef; color: #c0284a; }
.ivt-status--partial { background: #fff4e0; color: #b26a00; }
.ivt-status--paid { background: #e6f8ef; color: #1e8a5a; }
.ivt-status--archived { background: #eceff1; color: #5f6b73; }

/* Sized to its items. Needed because FormRoleDropdown.vue ships an
   unscoped `.dropdown-menu { width: 100% }` that, once loaded, stretches
   every Bootstrap dropdown menu in the app across its container. */
.ivt-root >>> .ivt-row-actions__menu {
  width: auto !important;
  min-width: 140px;
  max-width: 200px;
}
.ivt-root >>> .ivt-row-actions__menu .dropdown-item {
  font-size: 12px;
  padding: 6px 14px;
}

.ivt-root >>> .ivt-row-actions__toggle {
  padding: 0 4px;
  color: #1c4a50;
  /* The theme gives every .btn a 36px min-height, which on its own made
     each Invoice-view row taller than the Itemized view's. */
  min-height: 0;
  height: 20px;
  line-height: 1;
  border: none;
}

.ivt-root >>> .container {
    padding: 0!important;
    max-width: initial!important;
}

.ivt-charges {
  background: #e8f4ee;
  padding: 12px 16px;
}
.ivt-charges__loading { text-align: center; color: #2f6b4f; padding: 12px; font-size: 12px; }
.ivt-charges__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}
.ivt-charges__table th {
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #2f6b4f;
  padding: 6px 10px;
  border-bottom: 1px solid #cfe8db;
}
.ivt-charges__filters th {
  padding: 4px 6px;
  border-bottom: 1px solid #cfe8db;
}
.ivt-charges__filter-cell { display: flex; align-items: center; gap: 4px; }
.ivt-charges__filter {
  width: 100%;
  min-width: 0;
  height: 24px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #32325d;
  background: #fff;
  border: 1px solid #cfe8db;
  border-radius: 4px;
}
.ivt-charges__filter:focus { outline: none; border-color: #2f6b4f; }
.ivt-charges__filter-clear {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: #c0284a;
  font-size: 12px;
  line-height: 1;
  padding: 0 2px;
  cursor: pointer;
}
.ivt-charges__table td {
  padding: 6px 10px;
  color: #32325d;
  border-bottom: 1px solid #d9efe3;
}
.ivt-mono { font-family: monospace; font-size: 10px; }
.ivt-charges__amount { text-align: right; font-weight: 600; }
.ivt-charges__empty { text-align: center; color: #8898aa; padding: 12px; }
.ivt-charges__subtotal-label { font-weight: 700; text-align: right; padding: 8px 10px; border-bottom: none; }
.ivt-charges__subtotal-value { font-weight: 700; border-bottom: none; }
</style>
